<template>
  <div class="pa-4">
    <v-card elevation="0" style="margin-top:10px" class="pa-1">
      <v-card-text class="text-center" v-if="loading">
        <circles-loader></circles-loader>
      </v-card-text>
      <div class="pl-2 mt-2">
        <b>{{ $t('message.title') }}</b>: {{ templateName }}
      </div>
      <div class="pl-2">
        <b>{{ $t('message.total_questions') }}</b>: {{ questions ? questions.length : 0 }}
      </div>
      <div class="pl-2">
        <b>{{ $t('message.total_users') }}</b>: {{ totalUsers }}
      </div>
      <v-card-title>
        <v-select outlined dense v-if="questions" @change="getQuestionAggregates()"
          :label="$t('message.select_question_see_data')" :items="questions" v-model="selectedQuestion"></v-select>
      </v-card-title>
      <v-card-actions v-if="loadingQuestionData">
        <v-spacer></v-spacer>
        <circles-loader></circles-loader>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
    <v-card elevation="0" v-if="aggregatedData" class="bjjlink-card">
      <v-card-text v-if="aggregatedData.noData">
        {{ $t('message.no_data') }}
      </v-card-text>
      <v-card-text v-if="pieChartData">
        <span v-if="aggregatedData.required" class="supporting-text">*{{ $t('message.required') }} </span>
        <ApexChart v-if="pieChartData" type="pie" :options="pieChartOptions" height="350" :series="pieChartData">
        </ApexChart>
        <div class="supporting-text text-center">{{ $t('message.only_latest_answer') }} </div>
      </v-card-text>
      <v-list v-if="aggregatedData.responses">
        <v-subheader>
          <CheckCircleIcon size="20" class="hero-icon-button" color="gray"></CheckCircleIcon>
          {{ $t('message.individual_responses') }} ({{ aggregatedData.responses.length }})
        </v-subheader>
        <template v-for="(i, index) in aggregatedData.responses">
          <v-card outlined :key="'user_answer_' + index">
            <v-list-item class="text-left">
              <v-list-item-avatar v-if="i.user" @click="goToMember(i.user._id, i.user.displayName)">
                <v-avatar size="20" class="clickable">
                  <img v-if="i.user.userImageSmall" :src="userImg(i.user.userImageSmall)" />
                  <img v-else :src="assets('default-profile-avatar.png')" />
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title v-if="i.user" class="supporting-text">
                  {{ i.user.displayName }}
                </v-list-item-title>
                <v-list-item-title class="supporting-text">
                  {{ $d(new Date(i.completed), 'long', $i18n.locale) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <div class="ml-2">
              <v-card-text>
                <b>{{ $t('message.answer') }}: </b>
                <div v-if="i.answer" class="supporting-text page-description">{{ i.answer }}</div><span v-else>{{
                  $t('message.' + i.answerBoolean)
                }}</span>
              </v-card-text>
            </div>
          </v-card>
        </template>
      </v-list>
    </v-card>
  </div>
</template>

<script>
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import Api from "@/services/Api2";
import ApexChart from 'vue-apexcharts';

import {
  CheckCircleIcon,
} from '@vue-hero-icons/outline';

export default {
  data() {
    return {
      templateId: this.$route.params.templateId,
      loading: true,
      aggregatedData: null,
      questions: null,
      templateName: null,
      totalUsers: 0,
      selectedQuestion: null,
      loadingQuestionData: false,
      pieChartData: null,
      pieChartOptions: {
        legend: { position: "right", alignment: "center" },
        colors: [this.getColor('blue'), this.getColor('purple'), this.getColor('brown'), this.getColor('black'), this.getColor('red')],
      },
    };
  },
  components: {
    CirclesLoader,
    CheckCircleIcon,
    ApexChart,
  },
  created() {
    this.getAggregated();
  },
  watch: {},
  methods: {
    getQuestionAggregates() {
      this.loadingQuestionData = true;
      Api()
        .get("documents/academy/" +
          this.$store.state.managedAcademy +
          "/surveys/aggregates/question/" + this.selectedQuestion)
        .then((response) => {
          if (response.data) {
            this.loadingQuestionData = false;
            if (response.data.aggregates) {
              this.aggregatedData = response.data.aggregates;
              if (this.aggregatedData.chart) {
                let tmp = [];
                let labels = [];
                for (const prop in this.aggregatedData.chart) {
                  if (this.aggregatedData.type == 'boolean') {
                    tmp.push(this.aggregatedData.chart[prop]);
                    labels.push(this.$t('message.' + prop));
                  } else {
                    tmp.push(this.aggregatedData.chart[prop]);
                    labels.push(prop);
                  }
                }
                this.pieChartOptions = {
                  theme: {
                    monochrome: {
                      enabled: true,
                      color: this.themeColor('secondary'),
                      shadeTo: 'light',
                      shadeIntensity: 0.65
                    }
                  },
                  legend: {
                    position: 'bottom'
                  },
                  labels: labels
                };
                this.pieChartData = tmp;
              } else {
                this.pieChartData = null;
              }
            } else {
              this.aggregatedData = { noData: true };
            }
          }
        })
        .catch(() => {
          this.loadingQuestionData = false;
          this.showAlert("error", "Oops!", this.$t('message.error_generic'));
        });
    },
    getAggregated() {
      Api()
        .get("documents/academy/" +
          this.$store.state.managedAcademy +
          "/surveys/aggregates/" +
          this.templateId)
        .then((response) => {
          if (response.data) {
            this.loading = false;
            if (response.data.template) {
              this.templateName = response.data.template.name;
              this.totalUsers = response.data.userCount;
              if (response.data.template.questions && response.data.template.questions.length > 0) {
                let tmp = [];
                for (const q of response.data.template.questions)
                  tmp.push({ text: q.question, value: q._id });
                this.questions = tmp;
              }
            }
          }
        })
        .catch(() => {
          this.loading = false;
          this.showAlert("error", "Oops!", this.$t('message.error_generic'));
        });
    },
  }
};
</script>

<style scoped></style>